import {
  EXAM_TYPE,
  EPISODE_STATUS,
  PROCESSING_STATUS,
  contentType,
  courseStatus,
} from '@shared/const';
import { newSelectTranslation } from '@shared/utils';

import { validateSubtitleLanguages } from '../../organisms/ManageCourseView/utils/episodeValidation';

import TABS from './tabs';

const validateFile = (entity) => {
  return entity?.file?.processingStatus === PROCESSING_STATUS.PROCESSED;
};

const validateImageFile = (entity, isPublishedCourse = false) => {
  // For published courses, accept both PROCESSED and TO_SCHEDULE for images
  // (they don't have refresh functionality like videos do)
  if (isPublishedCourse) {
    return (
      entity?.file?.processingStatus === PROCESSING_STATUS.PROCESSED ||
      entity?.file?.processingStatus === PROCESSING_STATUS.TO_SCHEDULE
    );
  }
  return validateFile(entity);
};

const validateAttachments = (entity, isPublishedCourse = false) => {
  return (entity.attachments || []).reduce((acc, attachment) => {
    // For attachments, use validateImageFile logic (they don't have refresh UI)
    const isValid = isPublishedCourse
      ? validateImageFile(attachment, true)
      : validateFile(attachment);

    if (!isValid) {
      acc[attachment.id] = attachment.name;
    }
    return acc;
  }, {});
};

export const validateDescription = (course, locales) => {
  const validation = {};
  const usedLocales = [locales[0]];

  // If prerequisites or typical learners are custom in a locale, they must be
  // custom in every OTHER locale that already has a prerequisites/typical
  // learner entry of its own (i.e. someone has previously engaged that
  // locale's edit field for it). A locale that has never had this field
  // touched isn't held to that rule just because a different locale's
  // prerequisites were edited.
  const hasAnyPrerequisites = course.prerequisites?.some(
    ({ value }) => value !== null && value !== ''
  );
  const hasAnyTypicalLearner = course.typicalLearner?.some(
    ({ value }) => value !== null && value !== ''
  );

  const hasPrerequisitesEntry = (locale) =>
    course.prerequisites?.some((item) => item.languageCode === locale);
  const hasTypicalLearnerEntry = (locale) =>
    course.typicalLearner?.some((item) => item.languageCode === locale);

  locales.forEach((locale, index) => {
    const localeValidation = {
      description:
        course.description &&
        !!newSelectTranslation(course.description, locale, '').trim(),
      name:
        course.name && !!newSelectTranslation(course.name, locale, '').trim(),
      shortDescription:
        course.shortDescription &&
        !!newSelectTranslation(course.shortDescription, locale, '').trim(),
      learningPoints:
        course.learningPoints &&
        newSelectTranslation(course.learningPoints, locale, [])
          .map((leaningPoint) => leaningPoint.trim())
          .filter((leaningPoint) => leaningPoint).length > 0,
      ...(hasAnyPrerequisites && hasPrerequisitesEntry(locale)
        ? {
            prerequisites: !!newSelectTranslation(
              course.prerequisites,
              locale,
              ''
            ).trim(),
          }
        : {}),
      ...(hasAnyTypicalLearner && hasTypicalLearnerEntry(locale)
        ? {
            typicalLearner: !!newSelectTranslation(
              course.typicalLearner,
              locale,
              ''
            ).trim(),
          }
        : {}),
    };

    let allMissing = true;

    Object.entries(localeValidation).forEach(([key, value]) => {
      if (!value) {
        if (!validation[locale]) {
          validation[locale] = {};
        }
        validation[locale][key] = true;
      } else {
        allMissing = false;
      }
    });

    if (index !== 0 && !allMissing) {
      usedLocales.push(locale);
    } else if (index !== 0) {
      delete validation[locale];
    }
  });

  return {
    usedLocales,
    validation,
  };
};

const validateGameOrder = (courseEpisodes) => {
  // Validate that between two game episodes there is a non game episode
  const gameEpisodes = new Set(
    courseEpisodes
      .filter((episode) => episode.contentType === contentType.GAME)
      .map(({ ordinal }) => ordinal)
  );

  for (let i = 1; i < gameEpisodes.length; i += 1) {
    if (gameEpisodes[i] - gameEpisodes[i - 1] === 1) {
      return false;
    }
  }
  return true;
};

const validateLessons = (course, locales, originalWithoutSubtitles) => {
  const episodes = course.episodes.filter(
    (episode) => episode.status === EPISODE_STATUS.ACTIVE
  );
  const validation = {};
  let hasPendingSubtitles = false;
  const isPublished = course?.status === courseStatus.PUBLISHED;

  if (!validateFile(course.trailer)) {
    validation.trailer = true;
  }

  if (!(episodes?.length > 0)) {
    validation.noEpisodes = true;
  } else {
    if (!validateGameOrder(episodes)) {
      validation.gameOrder = true;
    }

    const subtitleValidation = validateSubtitleLanguages(episodes);
    episodes.forEach((episode) => {
      const episodeValidation = {};

      locales
        .filter(
          (locale) =>
            !episode.name ||
            !newSelectTranslation(episode.name, locale, '').trim()
        )
        .forEach((locale) => {
          episodeValidation.description = {
            [locale]: {
              title: true,
            },
          };
        });

      const { content } = episode;

      if (!content) {
        episodeValidation.video = true;
      } else if (episode.contentType !== contentType.GAME) {
        if (!validateFile(content)) {
          episodeValidation.video = true;
        }

        const hasSubtitles = content.subtitles && content.subtitles.length > 0;

        if (!hasSubtitles) {
          episodeValidation.subtitle = true;
        } else {
          const hasErrorSubtitles = content.subtitles.some(
            (subtitle) =>
              subtitle?.file?.processingStatus === PROCESSING_STATUS.ERROR
          );

          const hasProcessingSubtitles = content.subtitles.some(
            (subtitle) =>
              subtitle?.file?.processingStatus !== PROCESSING_STATUS.PROCESSED
          );

          if (hasErrorSubtitles) {
            episodeValidation.subtitleError = true;
            hasPendingSubtitles = true;
          } else if (hasProcessingSubtitles) {
            episodeValidation.subtitleProcessing = true;
            hasPendingSubtitles = true;
          }

          if (subtitleValidation.isMixed) {
            episodeValidation.subtitleMixed = true;
          }
        }
      } else {
        if (!content.externalGameId) {
          episodeValidation.gameId = true;
        }
        if (!content.duration) {
          episodeValidation.gameLength = true;
        }
      }

      const attachmentValidation = validateAttachments(episode, isPublished);

      if (Object.keys(attachmentValidation).length > 0) {
        episodeValidation.attachments = attachmentValidation;
      }

      if (Object.keys(episodeValidation).length > 0) {
        if (!validation.episodes) {
          validation.episodes = [];
        }
        episodeValidation.index = episode.ordinal;
        validation.episodes.push(episodeValidation);
      }
    });
  }

  const isAllowedSubtitleState = () => {
    const { isMixed, allNone } = validateSubtitleLanguages(course.episodes);

    if (!isPublished) {
      return !isMixed;
    }

    if (originalWithoutSubtitles === undefined) {
      return false;
    }

    if (originalWithoutSubtitles) {
      return allNone || !isMixed;
    }

    return !allNone && !isMixed;
  };

  const shouldShowSubtitleErrors =
    !isAllowedSubtitleState() || hasPendingSubtitles;

  validation.episodes = validation.episodes
    ?.map((episodeValidation) => {
      if (shouldShowSubtitleErrors) {
        return episodeValidation;
      }

      const { subtitle, subtitleMixed, ...rest } = episodeValidation;

      return Object.keys(rest).length > 1 ? rest : undefined;
    })
    .filter(Boolean);

  if (!validation.episodes || validation.episodes.length === 0) {
    delete validation.episodes;
  }

  return validation;
};

const validateCatalog = (course, videocationCatalog, originalCourse = null) => {
  const validation = {};
  const isPublishedCourse = originalCourse?.status === courseStatus.PUBLISHED;

  const hasVideocationCatalog = course.catalogs?.some(
    (catalog) => catalog.id === videocationCatalog?.id
  );

  if (hasVideocationCatalog && !(course.experts?.length > 0)) {
    validation.experts = true;
  }

  // Use validateImageFile for promotional image
  if (
    course.experts?.length > 1 &&
    !validateImageFile(course.promoPicture, isPublishedCourse)
  ) {
    validation.promo = true;
  }

  // Use validateImageFile for thumbnail
  if (!validateImageFile(course.thumbnail, isPublishedCourse)) {
    validation.thumbnail = true;
  }

  if (originalCourse && originalCourse?.status === courseStatus.PUBLISHED) {
    if (
      originalCourse.promoPicture &&
      !validateImageFile(course.promoPicture, true)
    ) {
      validation.promo = true;
    }
    if (
      originalCourse.thumbnail &&
      !validateImageFile(course.thumbnail, true)
    ) {
      validation.thumbnail = true;
    }
  }

  return validation;
};

const validateExam = (course, locales) => {
  const validation = {};

  if (
    !course.exam ||
    // eslint-disable-next-line no-underscore-dangle
    course.exam.__typename.toLowerCase() === EXAM_TYPE.GAME ||
    !course.exam.questions ||
    course.exam.questions.length === 0
  ) {
    return validation;
  }

  const { questions } = course.exam;
  const missingTranslations = [];

  questions.forEach((question, questionIndex) => {
    const questionMissing = [];
    const answersMissing = [];

    locales.forEach((locale) => {
      const questionTranslation = question.question?.find(
        (q) => q.languageCode === locale
      );
      if (!questionTranslation || !questionTranslation.value?.trim()) {
        questionMissing.push(locale);
      }
    });

    question.answers?.forEach((answer, answerIndex) => {
      const answerLocalesMissing = [];

      locales.forEach((locale) => {
        const answerTranslation = answer.answer?.find(
          (a) => a.languageCode === locale
        );
        if (!answerTranslation || !answerTranslation.value?.trim()) {
          answerLocalesMissing.push(locale);
        }
      });

      if (answerLocalesMissing.length > 0) {
        answersMissing.push({
          answerIndex,
          locales: answerLocalesMissing,
        });
      }
    });

    if (questionMissing.length > 0 || answersMissing.length > 0) {
      missingTranslations.push({
        questionIndex,
        questionMissing,
        answersMissing,
      });
    }
  });

  if (missingTranslations.length > 0) {
    validation.missingTranslations = missingTranslations;
  }

  return validation;
};

export default (
  course,
  locales,
  originalWithoutSubtitles,
  videocationCatalog,
  originalCourse = null
) => {
  const { usedLocales, validation: descriptionValidations } =
    validateDescription(course, locales);
  const lessonsValidations = validateLessons(
    course,
    usedLocales,
    originalWithoutSubtitles
  );
  const catalogValidations = validateCatalog(
    course,
    videocationCatalog,
    originalCourse
  );
  const examValidations = validateExam(course, usedLocales);
  const isPublishedCourse = originalCourse?.status === courseStatus.PUBLISHED;
  const attachmentValidations = validateAttachments(course, isPublishedCourse);

  const validation = {};

  if (Object.keys(descriptionValidations).length > 0) {
    validation[TABS.DESCRIPTION] = descriptionValidations;
  }

  if (Object.keys(lessonsValidations).length > 0) {
    validation[TABS.LESSONS] = lessonsValidations;
  }

  if (Object.keys(catalogValidations).length > 0) {
    validation[TABS.CATALOG] = catalogValidations;
  }

  if (Object.keys(examValidations).length > 0) {
    validation[TABS.EXAM] = examValidations;
  }

  if (Object.keys(attachmentValidations).length > 0) {
    validation[TABS.ATTACHMENTS] = attachmentValidations;
  }

  return validation;
};
